/**
 * OpenAICredentialModal — OpenAI (Codex) deploy credential.
 *
 * Two modes:
 *
 *   1. CAPTURE-ONLY (`captureOnly` prop) — mirrors ClaudeCredentialModal.
 *      Used by the managed-app deploy flow when the selected project has no
 *      OpenAI key yet: paste an `sk-…` key for a SINGLE deploy. The key is
 *      validated locally and handed back via `onCapture({ token, mode:'api' })`.
 *      The caller injects it into the deploy POST body as the vendor-neutral
 *      `agentToken` (per-app, ephemeral) and drops it immediately. NOTHING is
 *      written to the account store or the project row from here.
 *
 *   2. INFORMATIONAL (default) — explains that Codex creds live per-project
 *      and links to Project Settings → AI where the user sets `aiApiKey`.
 *
 * OpenAI v1 is API-key ONLY (no OAuth subscription token), so there is a
 * single input + a fixed mode of 'api'.
 *
 * Props: { isOpen, onClose, projectId, captureOnly?, onCapture({token,mode}) }
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../molecules/ConfirmModal/ConfirmModal';
import PillButton from '../shared/PillButton';

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)',
  color: '#fff', fontSize: 14, boxSizing: 'border-box',
};

export default function OpenAICredentialModal({ isOpen, onClose, projectId, captureOnly = false, onCapture }) {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) { setToken(''); setError(''); }
  }, [isOpen]);

  const goToSettings = () => {
    onClose?.();
    if (projectId) navigate(`/projects/${projectId}/settings`);
  };

  const handleSubmit = () => {
    const trimmed = token.trim();
    if (!trimmed) { setError('API key is required.'); return; }
    // Capture-only: validate the OpenAI key shape locally (same `/^sk-/`
    // the backend's deployApp enforces for provider="codex") + hand the
    // plaintext back. NEVER persisted (not to an account store, not to the
    // project). The caller injects it into the deploy as `agentToken`.
    if (!/^sk-/.test(trimmed)) {
      setError('OpenAI API key must start with sk-.');
      return;
    }
    onCapture?.({ token: trimmed, mode: 'api' });
    onClose?.();
  };

  // ── Capture-only (paste a key for THIS deploy) ──────────────────────────
  if (captureOnly) {
    return (
      <ConfirmModal
        open={isOpen}
        onClose={onClose}
        title="Connect OpenAI"
        ariaLabel="Connect OpenAI"
        secondaryLabel=""
      >
        <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: 0, lineHeight: 1.5 }}>
          Teamy needs an OpenAI API key to run your app&apos;s Codex agent. The
          key is used only for this deploy and stored encrypted on the app — it
          is never saved to your account. To set a reusable credential for every
          app in a project, add it in Project Settings &rarr; AI. Mint a key at{' '}
          <a
            href="https://platform.openai.com/api-keys"
            target="_blank"
            rel="noreferrer noopener"
            style={{ color: 'var(--ion-color-primary)' }}
          >
            platform.openai.com/api-keys
          </a>.
        </p>
        <label htmlFor="openai-cred-token" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          API key
        </label>
        <input
          id="openai-cred-token"
          type="password"
          value={token}
          placeholder="sk-…"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => { setToken(e.target.value); setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && token.trim()) { e.preventDefault(); handleSubmit(); } }}
          style={inputStyle}
        />
        {error && <p role="alert" style={{ color: '#f87171', fontSize: 13, marginTop: 8 }}>{error}</p>}
        <div style={{ marginTop: 16 }}>
          <PillButton onClick={handleSubmit} disabled={!token.trim()} className="btn-fullwidth">
            Use for this deploy
          </PillButton>
        </div>
      </ConfirmModal>
    );
  }

  // ── Informational (link to Project Settings) ────────────────────────────
  return (
    <ConfirmModal
      open={isOpen}
      onClose={onClose}
      title="Add an OpenAI key to this project"
      ariaLabel="Add an OpenAI key to this project"
      secondaryLabel=""
    >
      <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: 0, lineHeight: 1.5 }}>
        Deploying with the Codex agent needs an OpenAI API key on this
        project. Codex credentials are stored per-project (encrypted) in the
        project&apos;s AI Settings and reused by every app and workflow in the
        project — set it once there. Mint a key at{' '}
        <a
          href="https://platform.openai.com/api-keys"
          target="_blank"
          rel="noreferrer noopener"
          style={{ color: 'var(--ion-color-primary)' }}
        >
          platform.openai.com/api-keys
        </a>{projectId ? ', then add it under Project Settings → AI.' : '.'}
      </p>
      {projectId && (
        <div style={{ marginTop: 16 }}>
          <PillButton onClick={goToSettings} className="btn-fullwidth">
            Open Project Settings
          </PillButton>
        </div>
      )}
    </ConfirmModal>
  );
}
